import styled from '@emotion/styled';
import { captureException } from '@sentry/browser';
import React, { useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { useVisuallyHidden } from 'react-aria';

import { Modal } from 'src/shared/components/Modal';

import {
    ButtonGroup as DSButtonGroup,
    Button,
    BodyTextMedium,
    HeadingText40,
    MultiSelectButton as AnswerButtonForMultiSelect,
    pxToRem,
    FormInputTextarea,
    Form,
    useForm,
} from '@modernhealth/vital-components';

import { useReviewProgramMutation } from '../documents/reviewProgramMutation.generated';

const StyledModal = styled(Modal)({
    '.ant-modal-content': {
        boxShadow: '0 8px 16px 0 rgba(116, 135, 156, 0.5)',
        borderRadius: pxToRem(20),

        '.ant-modal-body': {
            padding: 0,
        },
    },
});

const Wrapper = styled.div({
    padding: pxToRem(32),
    paddingTop: pxToRem(40),
});

const List = styled.ul({
    padding: 0,
    marginTop: pxToRem(24),
});

const ButtonGroup = styled(DSButtonGroup)({
    marginTop: pxToRem(24),
});

const answers = [
    { displayText: 'programs:program.early_exit.answer1', value: 'Content was not helpful' },
    { displayText: 'programs:program.early_exit.answer2', value: 'Content was too long' },
    { displayText: 'programs:program.early_exit.answer3', value: 'Another reason' },
];

export enum EarlyExitFeedbackModalTestID {
    CloseButton = 'EarlyExitFeedbackModalTestID.CloseButton',
}

interface Props {
    visible: boolean;
    programUuid: string;
    onContinueProgram: () => void;
    onExit: () => void;
}

export const EarlyExitFeedbackModal: React.FC<Props> = ({
    visible,
    programUuid,
    onExit,
    onContinueProgram,
}) => {
    const { t } = useTranslation();
    const { visuallyHiddenProps } = useVisuallyHidden();

    const [reviewProgram, { loading }] = useReviewProgramMutation();

    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);

    const anotherReasonValue = answers[2].value;
    const anotherReasonSelected = selectedAnswer === anotherReasonValue;

    type FeedbackFormData = {
        dislikedReasons: string;
    };

    const formDefinition = useForm<FeedbackFormData>();

    const handleAnswerClick = (newValue: string) => {
        setSelectedAnswer(selectedAnswer === newValue ? null : newValue);
    };

    const handleSubmit = async (data: FeedbackFormData) => {
        if (loading || !selectedAnswer) {
            return;
        }

        try {
            await reviewProgram({
                variables: {
                    programUuid,
                    dislikedReasons:
                        anotherReasonSelected && data.dislikedReasons
                            ? [data.dislikedReasons]
                            : [selectedAnswer],
                    likedReasons: [],
                },
            });
            setSelectedAnswer(null);

            toastNotificationsService.renderSuccessToast('shared:thank_you_for_sharing');
            onExit();
        } catch (error) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);
            captureException(error);
        }
    };

    return (
        <StyledModal
            visible={visible}
            footer={null}
            onCancel={onContinueProgram}
            closeButtonTestID={EarlyExitFeedbackModalTestID.CloseButton}
            centered
        >
            <Wrapper>
                <HeadingText40 centered>{t('programs:program.early_exit.title')}</HeadingText40>
                <BodyTextMedium marginTop={12} fontColor="gray65" centered>
                    {t('programs:program.early_exit.tell_us_more')}
                </BodyTextMedium>

                <Form css={{ gridGap: 0 }} onSubmit={handleSubmit} definition={formDefinition}>
                    <List>
                        {answers.map(({ displayText, value }) => (
                            <AnswerButtonForMultiSelect
                                key={value}
                                text={t(displayText)}
                                onClick={() => handleAnswerClick(value)}
                                active={selectedAnswer === value}
                            />
                        ))}
                    </List>

                    <AnimateHeight
                        duration={300}
                        height={anotherReasonSelected ? 'auto' : 0}
                        style={{
                            width: '100%',
                            opacity: anotherReasonSelected ? 1 : 0,
                            transition: 'opacity 0.25s ease-in-out 0.1s',
                        }}
                    >
                        <div id="dislikedReasons" {...visuallyHiddenProps}>
                            {t('programs:program.early_exit.text_field_placeholder')}
                        </div>
                        <FormInputTextarea
                            rows={5}
                            placeholder={t('programs:program.early_exit.text_field_placeholder')}
                            name="dislikedReasons"
                            labelledBy="dislikedReasons"
                        />
                    </AnimateHeight>

                    <ButtonGroup>
                        <Button variant="secondary" size="large" onClick={onExit}>
                            {t('shared:skip')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            busy={loading}
                            disabled={!selectedAnswer}
                            type="submit"
                        >
                            {t('shared:submit')}
                        </Button>
                    </ButtonGroup>
                </Form>
            </Wrapper>
        </StyledModal>
    );
};
